import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { setSelectedTemplate } from '../../../features/templates/templatesSlice';
import { Template } from '../../../features/templates/templatesApi';

interface TemplateSectionProps {
  title: string;
  templates: Template[];
  isLoading?: boolean;
}

const Header = ({ title }: { title: string }) => {
  const navigate = useNavigate();
  return (
    <div className="content-stretch flex gap-[10px] h-[13px] items-center justify-center relative shrink-0 w-full mb-4">
      <p className="basis-0 font-sans font-bold grow leading-[normal] min-h-px min-w-px relative shrink-0 text-[#101928] text-[20px]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        {title}
      </p>
      <p
        onClick={() => navigate('/templates')}
        className="font-sans font-medium leading-[24px] relative shrink-0 text-[#227735] text-[13px] text-nowrap whitespace-pre cursor-pointer hover:underline"
        style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
      >
        View All
      </p>
    </div>
  );
};

const ImageGrid = ({
  templates,
  isLoading
}: {
  templates: Template[];
  isLoading?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleTemplateClick = (template: Template) => {
    dispatch(setSelectedTemplate({
      id: template._id,
      name: template.name,
      previewUrl: template.thumbnail,
      width: template.data.width,
      height: template.data.height,
      layers: template.data.layers,
      elements: template.data.elements,
      layerPositions: template.data.layerPositions,
      textContents: template.data.textContents,
      textStyles: template.data.textStyles,
      imageSettings: template.data.imageSettings,
      shapeSettings: template.data.shapeSettings,
      backgroundSettings: template.data.backgroundSettings,
      scrollerSettings: template.data.scrollerSettings
    }));
    navigate('/template-editor');
  };

  if (isLoading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4 w-full">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-[137px] w-[230px] animate-pulse bg-gray-100 rounded-[4px] shrink-0" />
        ))}
      </div>
    );
  }

  return (
    <div className="content-stretch flex flex-nowrap gap-[16px] items-center relative shrink-0 w-full overflow-x-auto pb-2 scrollbar-hide">
      {templates.map((template) => (
        <div
          key={template._id}
          className="h-[137px] relative rounded-[4px] shrink-0 basis-[230px] cursor-pointer hover:shadow-lg transition-all group overflow-hidden border border-gray-100"
          onClick={() => handleTemplateClick(template)}
        >
          <img
            alt={template.name}
            className="absolute inset-0 max-w-none object-cover pointer-events-none size-full transition-transform duration-500 group-hover:scale-105"
            src={template.thumbnail}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
        </div>
      ))}
      {templates.length === 0 && (
        <p className="text-gray-400 text-sm">No templates found in this category.</p>
      )}
    </div>
  );
};

export const TemplateSection = ({ title, templates, isLoading }: TemplateSectionProps) => {
  return (
    <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
      <Header title={title} />
      <ImageGrid templates={templates} isLoading={isLoading} />
    </div>
  );
};

